import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { CheckBox } from "../form-elements/checkbox.component";
import { InputBox } from "../form-elements/inputbox.component";
import { DropdownMenu } from "../form-elements/dropdownmenu.component";
import { additionalIncomeSources, usStates } from "./values";
import { InputBoxMoney } from "../form-elements/inputbox-money.component";
import { InputBoxNumber } from "../form-elements/inputbox-number.component";
import { InputBoxPhone } from "../form-elements/inputbox-phone.component";

interface EmploymentInformationFormProps {
  values?: any;
  errors?: any;
  touched?: any;
  handleChange?: any;
  handleBlur?: any;
  setFieldValue?: any;
}

export const initialValuesEmploymentInformation = {
  // step #3
  isRetired: false,
  retirementIncome: "",
  retirementYears: "",
  retirementMonths: "",

  isEmployed: false,
  employerName: "",
  employerPhoneNumber: "",
  occupationTitle: "",
  grossMonthlyIncome: "",
  employerAddressLine1: "",
  employerCity: "",
  employerState: "",
  employerZipCode: "",
  employedYears: "",
  employedMonths: "",
  previousEmployerName: "",
  // previousEmployerPhoneNumber: "",
  previousOccupationTitle: "",
  previousGrossMonthlyIncome: "",
  // previousEmployerAddressLine1: "",
  // previousEmployerCity: "",
  // previousEmployerState: "",
  // previousEmployerZipCode: "",
  previousEmployedYears: "",
  previousEmployedMonths: "",

  isAdditionalIncome: false,
  additionalIncomeSource: "",
  additionalIncomeSourceOther: "",
  additionalMonthlyIncome: "",
};

export const EmploymentInformationForm: React.FC<EmploymentInformationFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  const [shouldShowPreviousEmployment, setShouldShowPreviousEmployment] = useState(false);

  useEffect(() => {
    if (values.isRetired === false) {
      setFieldValue("retirementIncome", initialValuesEmploymentInformation.retirementIncome);
      setFieldValue("retirementYears", initialValuesEmploymentInformation.retirementYears);
      setFieldValue("retirementMonths", initialValuesEmploymentInformation.retirementMonths);
    }
  }, [setFieldValue, values.isRetired]);

  // Update this useEffect to implement the new rules
  useEffect(() => {
    // if retired and no retirement years or months, skip
    if (values.isRetired === true && !(values.retirementYears || values.retirementMonths)) {
      return;
    }
    // if employed and no employed years or months, skip
    if (values.isEmployed === true && !(values.employedYears || values.employedMonths)) {
      return;
    }

    // tricky requirements:
    // Previous Employment fields requirements:

    // SHOW PREVIOUS EMPLOYMENT
    // Retired less than 2 years AND not employed
    // Retired less than 2 years AND employed (LESS THAN 2 YEARS)
    // Employed less than 2 years AND not retired
    // Employed less than 2 years AND retired (LESS THAN 2 YEARS)

    // HIDE PREVIOUS EMPLOYMENT
    // Retired greater than 2 years AND not employed
    // Retired greater than 2 years AND employed greater than 2 years
    // Retired less than 2 years AND employed greater than 2 years
    // Employed greater than 2 years AND not retired
    // Employed greater than 2 years AND retired greater than 2 years
    // Employed less than 2 years AND retired greater than 2 years

    const _isRetiredLessThanTwoYears =
      values.isRetired &&
      (values.retirementYears || values.retirementMonths) &&
      Number(values.retirementYears) * 12 + Number(values.retirementMonths) < 24;

    const _isRetiredGreaterThanTwoYears =
      values.isRetired &&
      (values.retirementYears || values.retirementMonths) &&
      Number(values.retirementYears) * 12 + Number(values.retirementMonths) >= 24;

    const _isEmployedLessThanTwoYears =
      values.isEmployed &&
      (values.employedYears || values.employedMonths) &&
      Number(values.employedYears) * 12 + Number(values.employedMonths) < 24;

    const _isEmployedGreaterThanTwoYears =
      values.isEmployed &&
      (values.employedYears || values.employedMonths) &&
      Number(values.employedYears) * 12 + Number(values.employedMonths) >= 24;

    // Show previous employment ONLY if these conditions are met
    const _shouldShowPreviousEmployment =
      // Show cases
      ((_isRetiredLessThanTwoYears && !values.isEmployed) || // Retired < 2 years AND not employed
        (_isRetiredLessThanTwoYears && _isEmployedLessThanTwoYears) || // Retired < 2 years AND employed < 2 years
        (_isEmployedLessThanTwoYears && !values.isRetired) || // Employed < 2 years AND not retired
        (_isEmployedLessThanTwoYears && _isRetiredLessThanTwoYears)) && // Employed < 2 years AND retired < 2 years
      // AND none of the hide cases are true
      !(_isRetiredGreaterThanTwoYears || _isEmployedGreaterThanTwoYears); // Hide if either is > 2 years

    if (!_shouldShowPreviousEmployment) {
      // Reset previous employment fields if not needed
      setFieldValue("previousEmployerName", initialValuesEmploymentInformation.previousEmployerName);
      // setFieldValue("previousEmployerPhoneNumber", initialValuesEmploymentInformation.previousEmployerPhoneNumber);
      setFieldValue("previousOccupationTitle", initialValuesEmploymentInformation.previousOccupationTitle);
      setFieldValue("previousGrossMonthlyIncome", initialValuesEmploymentInformation.previousGrossMonthlyIncome);
      setFieldValue("previousEmployedYears", initialValuesEmploymentInformation.previousEmployedYears);
      setFieldValue("previousEmployedMonths", initialValuesEmploymentInformation.previousEmployedMonths);
    }

    setShouldShowPreviousEmployment(_shouldShowPreviousEmployment);
  }, [
    setFieldValue,
    values.retirementYears,
    values.retirementMonths,
    values.employedYears,
    values.employedMonths,
    values.isRetired,
    values.isEmployed,
  ]);

  // if not employed, set the initial values
  useEffect(() => {
    if (values.isEmployed === false) {
      setFieldValue("employerName", initialValuesEmploymentInformation.employerName);
      setFieldValue("employerPhoneNumber", initialValuesEmploymentInformation.employerPhoneNumber);
      setFieldValue("occupationTitle", initialValuesEmploymentInformation.occupationTitle);
      setFieldValue("grossMonthlyIncome", initialValuesEmploymentInformation.grossMonthlyIncome);
      setFieldValue("employerAddressLine1", initialValuesEmploymentInformation.employerAddressLine1);
      setFieldValue("employerCity", initialValuesEmploymentInformation.employerCity);
      setFieldValue("employerState", initialValuesEmploymentInformation.employerState);
      setFieldValue("employerZipCode", initialValuesEmploymentInformation.employerZipCode);
      setFieldValue("employedYears", initialValuesEmploymentInformation.employedYears);
      setFieldValue("employedMonths", initialValuesEmploymentInformation.employedMonths);
    }
  }, [setFieldValue, values.isEmployed]);

  // if additional income is not selected, set the initial values
  useEffect(() => {
    if (values.isAdditionalIncome === false) {
      setFieldValue("additionalIncomeSource", initialValuesEmploymentInformation.additionalIncomeSource);
      setFieldValue("additionalIncomeSourceOther", initialValuesEmploymentInformation.additionalIncomeSourceOther);
      setFieldValue("additionalMonthlyIncome", initialValuesEmploymentInformation.additionalMonthlyIncome);
    }
  }, [setFieldValue, values.isAdditionalIncome]);

  // if additional income is not Other, set the initial values
  useEffect(() => {
    if (values.additionalIncomeSource.toLowerCase() !== "other") {
      setFieldValue("additionalIncomeSourceOther", initialValuesEmploymentInformation.additionalIncomeSourceOther);
    }
  }, [setFieldValue, values.additionalIncomeSource]);

  return (
    <>
      <Row>
        <Col>
          <h3>Employment Information</h3>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <CheckBox
            label="Click here if Retired or Disabled"
            name="isRetired"
            // value={values.isRetired}
            valueForm={values.isRetired}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched.isRetired && !errors.isRetired}
            isInvalid={touched.isRetired && errors.isRetired}
            errorFeedback={errors.isRetired}
          />
          {/* <small className="d-block pt-2">
            If you are also currently employed, please complete the Employment Information section as well.
          </small> */}
        </Col>
      </Row>

      {values.isRetired ? (
        <>
          <Row>
            <Col md={6} lg={4}>
              <InputBoxMoney
                label="Retirement Income:"
                name="retirementIncome"
                value={values.retirementIncome}
                onChange={handleChange}
                setFieldValue={setFieldValue}
                onBlur={handleBlur}
                isValid={touched.retirementIncome && !errors.retirementIncome}
                isInvalid={touched.retirementIncome && errors.retirementIncome}
                errorFeedback={errors.retirementIncome}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <label>How Long have you been retired? </label>
            </Col>
          </Row>
          <Row>
            <Col sm={6} lg={4}>
              <InputBoxNumber
                label="Years:"
                name="retirementYears"
                value={values.retirementYears}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.retirementYears && !errors.retirementYears}
                isInvalid={touched.retirementYears && errors.retirementYears}
                errorFeedback={errors.retirementYears}
              />
            </Col>
            <Col sm={6} lg={4}>
              <InputBoxNumber
                label="Months:"
                name="retirementMonths"
                value={values.retirementMonths}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.retirementMonths && !errors.retirementMonths}
                isInvalid={touched.retirementMonths && errors.retirementMonths}
                errorFeedback={errors.retirementMonths}
              />
            </Col>
          </Row>
        </>
      ) : null}

      <Row>
        <Col xs={12}>
          <CheckBox
            label="Click here if Currently Employed"
            name="isEmployed"
            // value={true}
            valueForm={values.isEmployed}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched.isEmployed && !errors.isEmployed}
            isInvalid={touched.isEmployed && errors.isEmployed}
            errorFeedback={errors.isEmployed}
          />
        </Col>
      </Row>

      {values.isEmployed ? (
        <>
          <Row>
            <Col xs={12} md={6}>
              <InputBox
                label="Employer Business Name"
                name="employerName"
                value={values.employerName}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.employerName && !errors.employerName}
                isInvalid={touched.employerName && errors.employerName}
                errorFeedback={errors.employerName}
                length={100}
              />
            </Col>
            <Col xs={12} md={6}>
              <InputBoxPhone
                label="Business Phone"
                name="employerPhoneNumber"
                value={values.employerPhoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.employerPhoneNumber && !errors.employerPhoneNumber}
                isInvalid={touched.employerPhoneNumber && errors.employerPhoneNumber}
                errorFeedback={errors.employerPhoneNumber}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <InputBox
                label="Occupation Title"
                name="occupationTitle"
                value={values.occupationTitle}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.occupationTitle && !errors.occupationTitle}
                isInvalid={touched.occupationTitle && errors.occupationTitle}
                errorFeedback={errors.occupationTitle}
              />
            </Col>
            <Col xs={12} lg={6}>
              <InputBoxMoney
                label="Gross Monthly Income"
                name="grossMonthlyIncome"
                value={values.grossMonthlyIncome}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.grossMonthlyIncome && !errors.grossMonthlyIncome}
                isInvalid={touched.grossMonthlyIncome && errors.grossMonthlyIncome}
                errorFeedback={errors.grossMonthlyIncome}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <InputBox
                label="Employer Address"
                name="employerAddressLine1"
                value={values.employerAddressLine1}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.employerAddressLine1 && !errors.employerAddressLine1}
                isInvalid={touched.employerAddressLine1 && errors.employerAddressLine1}
                errorFeedback={errors.employerAddressLine1}
                length={100}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} sm={12} md={4}>
              <InputBox
                label="City"
                name="employerCity"
                value={values.employerCity}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.employerCity && !errors.employerCity}
                isInvalid={touched.employerCity && errors.employerCity}
                errorFeedback={errors.employerCity}
              />
            </Col>
            <Col xs={12} sm={6} md={4} lg={4}>
              <DropdownMenu
                label="State"
                name="employerState"
                options={usStates}
                value={values.employerState}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.employerState && !errors.employerState}
                isInvalid={touched.employerState && errors.employerState}
                errorFeedback={errors.employerState}
              />
            </Col>
            <Col xs={12} sm={6} md={4} lg={4}>
              <InputBoxNumber
                label="Zip"
                name="employerZipCode"
                value={values.employerZipCode}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.employerZipCode && !errors.employerZipCode}
                isInvalid={touched.employerZipCode && errors.employerZipCode}
                errorFeedback={errors.employerZipCode}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <p>Length of Time Employed</p>
            </Col>
          </Row>

          <Row>
            <Col sm={6} lg={4}>
              <InputBoxNumber
                label="Years"
                name="employedYears"
                value={values.employedYears}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.employedYears && !errors.employedYears}
                isInvalid={touched.employedYears && errors.employedYears}
                errorFeedback={errors.employedYears}
              />
            </Col>
            <Col sm={6} lg={4}>
              <InputBoxNumber
                label="Months"
                name="employedMonths"
                value={values.employedMonths}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.employedMonths && !errors.employedMonths}
                isInvalid={touched.employedMonths && errors.employedMonths}
                errorFeedback={errors.employedMonths}
              />
            </Col>
          </Row>
        </>
      ) : null}

      {shouldShowPreviousEmployment ? (
        <>
          <Row>
            <Col xs={12}>
              <h3>Previous Employment Information</h3>
              <p>Previous employment is required if current employment or retirement time is less than two years.</p>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <InputBox
                label="Previous Employer Business Name"
                name="previousEmployerName"
                value={values.previousEmployerName}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.previousEmployerName && !errors.previousEmployerName}
                isInvalid={touched.previousEmployerName && errors.previousEmployerName}
                errorFeedback={errors.previousEmployerName}
                length={100}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={6} lg={4}>
              <InputBox
                label="Occupation Title"
                name="previousOccupationTitle"
                value={values.previousOccupationTitle}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.previousOccupationTitle && !errors.previousOccupationTitle}
                isInvalid={touched.previousOccupationTitle && errors.previousOccupationTitle}
                errorFeedback={errors.previousOccupationTitle}
              />
            </Col>
            <Col sm={6} lg={4}>
              <InputBoxMoney
                label="Gross Monthly Income:"
                name="previousGrossMonthlyIncome"
                value={values.previousGrossMonthlyIncome}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.previousGrossMonthlyIncome && !errors.previousGrossMonthlyIncome}
                isInvalid={touched.previousGrossMonthlyIncome && errors.previousGrossMonthlyIncome}
                errorFeedback={errors.previousGrossMonthlyIncome}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <p>Length of Time Employed</p>
            </Col>
          </Row>

          <Row>
            <Col sm={6} lg={4}>
              <InputBoxNumber
                label="Years"
                name="previousEmployedYears"
                value={values.previousEmployedYears}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.previousEmployedYears && !errors.previousEmployedYears}
                isInvalid={touched.previousEmployedYears && errors.previousEmployedYears}
                errorFeedback={errors.previousEmployedYears}
              />
            </Col>
            <Col sm={6} lg={4}>
              <InputBoxNumber
                label="Months"
                name="previousEmployedMonths"
                value={values.previousEmployedMonths}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.previousEmployedMonths && !errors.previousEmployedMonths}
                isInvalid={touched.previousEmployedMonths && errors.previousEmployedMonths}
                errorFeedback={errors.previousEmployedMonths}
              />
            </Col>
          </Row>
        </>
      ) : null}

      <Row>
        <Col xs={12}>
          <CheckBox
            label="If Additional Income, click here"
            name="isAdditionalIncome"
            // value={true}
            valueForm={values.isAdditionalIncome}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched.isAdditionalIncome && !errors.isAdditionalIncome}
            isInvalid={touched.isAdditionalIncome && errors.isAdditionalIncome}
            errorFeedback={errors.isAdditionalIncome}
          />
        </Col>
      </Row>

      {values.isAdditionalIncome ? (
        <>
          <Row>
            <Col xs={12}>
              <h3>Additional Income</h3>
            </Col>
          </Row>

          <Row>
            <Col xs={12} md={6}>
              <DropdownMenu
                label="Additional Income Source"
                name="additionalIncomeSource"
                options={additionalIncomeSources}
                value={values.additionalIncomeSource}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.additionalIncomeSource && !errors.additionalIncomeSource}
                isInvalid={touched.additionalIncomeSource && errors.additionalIncomeSource}
                errorFeedback={errors.additionalIncomeSource}
              />
            </Col>
            {values.additionalIncomeSource.toLowerCase() === "other" ? (
              <Col xs={12} md={6}>
                <InputBox
                  label="Other"
                  name="additionalIncomeSourceOther"
                  value={values.additionalIncomeSourceOther}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={touched.additionalIncomeSourceOther && !errors.additionalIncomeSourceOther}
                  isInvalid={touched.additionalIncomeSourceOther && errors.additionalIncomeSourceOther}
                  errorFeedback={errors.additionalIncomeSourceOther}
                />
              </Col>
            ) : null}

            <Col xs={12} md={6}>
              <InputBoxMoney
                label="Monthly Income:"
                name="additionalMonthlyIncome"
                value={values.additionalMonthlyIncome}
                onChange={handleChange}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                isValid={touched.additionalMonthlyIncome && !errors.additionalMonthlyIncome}
                isInvalid={touched.additionalMonthlyIncome && errors.additionalMonthlyIncome}
                errorFeedback={errors.additionalMonthlyIncome}
              />
            </Col>
          </Row>
        </>
      ) : null}
    </>
  );
};
